/**
 * Diagnóstico da conta: saúde, achados por severidade e o plano da IA.
 *
 * Os achados vêm de regras determinísticas no servidor; a IA só explica. A
 * saúde não é publicada sem gasto suficiente — dizer "100" para uma conta
 * parada seria mentir.
 */

import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, ChevronDown, ChevronUp, Loader2, RefreshCw, Sparkles, Stethoscope } from "lucide-react";
import { moeda } from "@/lib/meta-ads-format";
import type { Severidade } from "@shared/ads-diagnostic";

const SEV: Record<Severidade, { label: string; cls: string; dot: string }> = {
  alta: { label: "Alta", cls: "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30", dot: "bg-red-500" },
  media: { label: "Média", cls: "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30", dot: "bg-amber-500" },
  baixa: { label: "Baixa", cls: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40", dot: "bg-slate-400" },
  info: { label: "Info", cls: "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30", dot: "bg-blue-400" },
};

const CATEGORIA: Record<string, string> = { verba: "Verba", estrutura: "Estrutura", entrega: "Entrega", criativo: "Criativo", medicao: "Medição" };

function corDaSaude(s: number) {
  return s >= 80 ? "text-emerald-600" : s >= 55 ? "text-amber-600" : "text-red-600";
}

export function DiagnosticCard({ adAccountId, currency }: { adAccountId: string | undefined; currency: string | null }) {
  const [aberto, setAberto] = useState(true);
  const [expandido, setExpandido] = useState<Set<string>>(new Set());
  const diag = trpc.metaAds.diagnostic.run.useQuery({ adAccountId }, { staleTime: 5 * 60_000, enabled: !!adAccountId, retry: false });
  const explicar = trpc.metaAds.diagnostic.explain.useMutation();

  const alternar = (id: string) =>
    setExpandido((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const d = diag.data;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex items-center gap-2 font-medium"><Stethoscope className="h-4 w-4" /> Diagnóstico da conta</h2>
          <span className="text-xs text-muted-foreground">{d ? `últimos 7 dias · ${d.periodo}` : "últimos 7 dias"}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => void diag.refetch()} disabled={diag.isFetching} title="Rodar de novo">
              {diag.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAberto((v) => !v)}>{aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
          </div>
        </div>

        {aberto && (
          <div className="mt-4 space-y-4">
            {diag.isLoading && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Lendo campanhas, conjuntos e duas semanas de métricas…</p>}
            {diag.isError && <p className="text-sm text-red-600">{toUserFriendlyError(diag.error)}</p>}

            {d && (
              <>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
                    <Activity className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Saúde</p>
                      {d.saude != null ? (
                        <p className={`text-2xl font-semibold ${corDaSaude(d.saude)}`}>{d.saude}<span className="text-sm text-muted-foreground">/100</span></p>
                      ) : (
                        <p className="text-sm font-medium">Sem gasto suficiente</p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>Gasto nos 7 dias: <strong className="text-foreground">{moeda(d.gasto7dCents, currency)}</strong></p>
                    <p>
                      {d.resumo.alta > 0 && <span className="mr-2 text-red-600">{d.resumo.alta} alta</span>}
                      {d.resumo.media > 0 && <span className="mr-2 text-amber-600">{d.resumo.media} média</span>}
                      {d.resumo.baixa > 0 && <span className="mr-2">{d.resumo.baixa} baixa</span>}
                      {d.achados.length === 0 && <span className="text-emerald-600">Nenhum sinal de problema.</span>}
                    </p>
                    {d.cobertura !== "ok" && <p className="text-xs">A conta gastou pouco na semana; os sinais de desempenho não valem ainda.</p>}
                  </div>
                  <div className="ml-auto">
                    <Button size="sm" onClick={() => explicar.mutate({ saude: d.saude, cobertura: d.cobertura, gasto7dCents: d.gasto7dCents, currency: d.achados.length ? currency : currency, achados: d.achados })} disabled={explicar.isPending || d.achados.length === 0}>
                      {explicar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Explicar com IA
                    </Button>
                  </div>
                </div>

                {explicar.isError && <p className="text-xs text-red-600">{toUserFriendlyError(explicar.error)}</p>}
                {explicar.data && (
                  <div className="whitespace-pre-wrap rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm leading-relaxed">{explicar.data.plano}</div>
                )}

                <div className="space-y-2">
                  {d.achados.map((a) => {
                    const sev = SEV[a.severidade];
                    const open = expandido.has(a.id);
                    return (
                      <div key={a.id} className={`rounded-lg border p-3 ${sev.cls}`}>
                        <button type="button" className="flex w-full items-start gap-3 text-left" onClick={() => alternar(a.id)}>
                          <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${sev.dot}`} />
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                              {a.titulo}
                              <Badge variant="outline" className="text-[10px]">{CATEGORIA[a.categoria] ?? a.categoria}</Badge>
                            </span>
                            {!open && <span className="mt-0.5 block text-xs text-muted-foreground">{a.acao}</span>}
                          </span>
                          {open ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </button>
                        {open && (
                          <div className="mt-2 space-y-2 pl-5 text-sm">
                            <p className="text-muted-foreground">{a.explicacao}</p>
                            <p><strong>O que fazer:</strong> {a.acao}</p>
                            {a.objetos.length > 0 && (
                              <ul className="space-y-1 text-xs">
                                {a.objetos.map((o) => (
                                  <li key={`${o.tipo}:${o.id}`} className="flex items-center gap-2">
                                    <Badge variant="secondary" className="text-[10px]">{o.tipo === "campaign" ? "campanha" : o.tipo === "adset" ? "conjunto" : "anúncio"}</Badge>
                                    {o.campaignId || o.tipo === "campaign" ? (
                                      <Link href={`/ads/campaigns/${o.tipo === "campaign" ? o.id : o.campaignId}`} className="hover:underline">{o.nome}</Link>
                                    ) : (
                                      <span>{o.nome}</span>
                                    )}
                                    {o.detalhe && <span className="text-muted-foreground">· {o.detalhe}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
